import { z } from "zod";
import type { SelectedLocation } from "./types";

/**
 * Client-safe definitions for the selected-location override cookie. Kept in
 * its own module (no `next/headers`) so client components can read/parse the
 * cookie without dragging server-only APIs into the browser bundle.
 */
export const SELECTED_LOCATION_COOKIE = "hiive_selected_location";

export const selectedLocationSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  displayName: z.string().trim().min(1).max(120),
  radiusKm: z.number().positive().max(500).optional(),
  source: z.enum(["profile", "custom", "current", "ip"]),
});

export function parseSelectedLocationCookie(
  raw: string,
): SelectedLocation | null {
  try {
    const parsed = selectedLocationSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Read the override cookie on the client. The cookie is not httpOnly, so this
 * resolves synchronously with no server round-trip — letting the location chip
 * reflect a custom override on first paint. Returns null on the server.
 */
export function readSelectedLocationCookie(): SelectedLocation | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${SELECTED_LOCATION_COOKIE}=`));
  if (!match) return null;
  const raw = decodeURIComponent(
    match.slice(SELECTED_LOCATION_COOKIE.length + 1),
  );
  return parseSelectedLocationCookie(raw);
}
